/**
 * **D174 — the localities Schedules offers, mirrored from the server's own table.**
 *
 * 🔸 This is a *mirror*, exactly like `MAX_OFFSET_NUDGE_MINUTES` above it in `ShabbatScreen.tsx`:
 * `packages/functions/state/localities.ts` is the authority, re-validates every `geonameid` on
 * `PUT /gates/{id}/shabbat`, and answers `422` for anything outside its table. Nothing here
 * decides which city is legal — it decides which options a manager can see, and the two must
 * agree or a manager is offered a city the server will refuse. **`infra/tests/shabbat-localities.test.ts`
 * is what makes them agree**; it reads both files and fails on any divergence, because a mirror
 * nobody checks is a copy that drifts.
 *
 * Not imported: `packages/functions/state` is a Lambda package and is not browser-bundleable.
 *
 * **The labels are ours.** Hebcal has no Hebrew city names at all — its own Hebrew UI renders
 * `Reẖovot` — so the Hebrew label could not have come from the feed, and the English one is
 * Hebcal's own rendering rather than a second transliteration of ours.
 */

/** One selectable city. `geonameid` is what `PUT /gates/{id}/shabbat` carries (D108: never a pin). */
export interface ShabbatLocality {
  readonly geonameid: number
  readonly labelHe: string
  readonly labelEn: string
}

/**
 * Declaration order is render order — a list a person scans, not the server's longest-term
 * match order. Jerusalem first because it is the one city with a candle custom of its own.
 */
export const SHABBAT_LOCALITIES: readonly ShabbatLocality[] = [
  { geonameid: 281184, labelHe: 'ירושלים', labelEn: 'Jerusalem' },
  { geonameid: 7498240, labelHe: 'ירושלים המערבית', labelEn: 'West Jerusalem' },
  { geonameid: 293397, labelHe: 'תל אביב-יפו', labelEn: 'Tel Aviv' },
  { geonameid: 294801, labelHe: 'חיפה', labelEn: 'Haifa' },
  { geonameid: 295530, labelHe: 'באר שבע', labelEn: 'Beersheba' },
  { geonameid: 293703, labelHe: 'ראשון לציון', labelEn: 'Rishon LeTsiyyon' },
  { geonameid: 294778, labelHe: 'הרצליה', labelEn: 'Herzliya' },
  { geonameid: 293807, labelHe: 'רעננה', labelEn: "Ra'anana" },
  { geonameid: 293918, labelHe: 'פתח תקווה', labelEn: 'Petah Tikva' },
  { geonameid: 294071, labelHe: 'נתניה', labelEn: 'Netanya' },
  { geonameid: 295629, labelHe: 'אשדוד', labelEn: 'Ashdod' },
  { geonameid: 295620, labelHe: 'אשקלון', labelEn: 'Ashkelon' },
  { geonameid: 295514, labelHe: 'בני ברק', labelEn: 'Bnei Brak' },
  { geonameid: 294514, labelHe: 'כפר סבא', labelEn: 'Kfar Saba' },
  { geonameid: 294999, labelHe: 'גבעתיים', labelEn: 'Givatayim' },
  { geonameid: 293322, labelHe: 'טבריה', labelEn: 'Tiberias' },
  { geonameid: 282926, labelHe: 'מודיעין-מכבים-רעות', labelEn: "Modi'in Maccabim Re'ut" },
  { geonameid: 293100, labelHe: 'צפת', labelEn: 'Safed' },
  { geonameid: 294751, labelHe: 'חולון', labelEn: 'Holon' },
  { geonameid: 293788, labelHe: 'רמת גן', labelEn: 'Ramat Gan' },
  { geonameid: 293842, labelHe: 'קריית גת', labelEn: 'Kiryat Gat' },
  { geonameid: 293725, labelHe: 'רחובות', labelEn: 'Rehovot' },
]

/**
 * The label for a `geo` the server sent back.
 *
 * Returns `undefined` rather than a placeholder for an unknown id, so the caller decides what to
 * say. That case is reachable in exactly one way — a gate configured against a newer server table
 * than this bundle carries — and inventing a name for it would be the mirror lying about a
 * divergence instead of showing it.
 */
/*
 * The table carries two labels, so every locale that is not Hebrew reads the Latin one. That is
 * right for Russian rather than merely tolerable: these are Israeli localities, and their Latin
 * transliterations ("Bnei Brak") are what a Russian speaker here recognises — a Cyrillic column
 * would be a third set of names to keep in step with the server's table for no gain.
 */
export function localityLabel(geonameid: number | undefined, locale: string): string | undefined {
  if (geonameid === undefined) return undefined
  const hit = SHABBAT_LOCALITIES.find((locality) => locality.geonameid === geonameid)
  if (hit === undefined) return undefined
  return locale === 'he' ? hit.labelHe : hit.labelEn
}
